import { useEffect, useRef, useState } from "react";
import { Trash } from "@phosphor-icons/react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DataRow,
  DataTable,
  Input,
  LoadingOverlay,
  initialsForMember,
} from "@breakingthelines/design-system";

import {
  addAuthorizedPublisher,
  listAuthorizedPublishers,
  listUsers,
  removeAuthorizedPublisher,
  searchSquads,
  type AuthorizedPublisher,
} from "@/lib/admin-api";

import styles from "./authorized-publishers.module.css";

// Minimum characters typed (after stripping a leading "@") before the search
// combobox queries listUsers/searchSquads — mirrors admins-page.tsx's own
// username search.
const MIN_SEARCH_LENGTH = 2;

// A principal search hit, regardless of which service resolved it. The
// combobox runs the user search and the squad search side by side for the
// same typed query and merges both into one option list, tagged by kind so
// each row can show a User/Squad label and so selecting either just needs
// this one id.
type PrincipalKind = "user" | "squad";

interface PrincipalOption {
  kind: PrincipalKind;
  id: string;
  handle?: string;
  displayName?: string;
}

function optionKey(option: PrincipalOption) {
  return `${option.kind}:${option.id}`;
}

function optionTypeLabel(kind: PrincipalKind) {
  return kind === "user" ? "User" : "Squad";
}

function useDebouncedValue<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);

  return debounced;
}

function formatDate(value?: string) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
}

// PublisherIdentityCell renders the resolved handle/displayName/avatarUrl a
// publisher carries — user or squad, admin-service resolves both (see
// admin-service's authorized_publishers.go) — with the raw id kept visible
// as a title tooltip and a subdued secondary line, never dropped entirely,
// since the id is still what the allowlist keys on. A publisher that
// resolves to neither (or an older admin-service deploy that doesn't resolve
// squads yet) carries no handle, so it falls back to the raw id.
function PublisherIdentityCell({ pub }: { pub: AuthorizedPublisher }) {
  if (!pub.handle) {
    return (
      <span className={styles.mono} data-label="Publisher" title={pub.publisherId}>
        {pub.publisherId}
      </span>
    );
  }

  const label = pub.displayName || pub.handle;

  return (
    <div className={styles.identityCell} data-label="Publisher" title={pub.publisherId}>
      {/* The design-system's Avatar falls back to the initials on its own when
          the image is missing or fails to load. */}
      <Avatar size="sm" className="size-5 shrink-0" aria-hidden="true">
        {pub.avatarUrl ? <AvatarImage src={pub.avatarUrl} alt="" loading="lazy" /> : null}
        <AvatarFallback className="text-[10px]">{initialsForMember(label)}</AvatarFallback>
      </Avatar>
      <div className={styles.identityText}>
        <span className={styles.identityName}>{label}</span>
        <span className={styles.identityMeta}>
          @{pub.handle}
          <span className={styles.identityId}> · {pub.publisherId}</span>
        </span>
      </div>
    </div>
  );
}

export function AuthorizedPublishersPage() {
  const [publishers, setPublishers] = useState<AuthorizedPublisher[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [newNote, setNewNote] = useState("");
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  // Grants for the ADD form only. Both default off: a new allowlist entry means
  // "never auto-hide", and must grant nothing for SEO unless asked for.
  const [newDofollow, setNewDofollow] = useState(false);
  const [newIndexing, setNewIndexing] = useState(false);
  // Defaults ON, unlike the two grants above. This flag RESTRICTS rather than
  // permits, and the allowlist records commercial arrangements — so disclosure
  // is the safe assumption for a new row.
  const [newSponsored, setNewSponsored] = useState(true);
  const [grantingId, setGrantingId] = useState<string | null>(null);

  // "Add by ID" is kept as an escape hatch alongside search: it covers any
  // principal type search can't reach (or a squad-search endpoint that
  // isn't deployed yet — see the degrade-gracefully handling below), even
  // though both users and squads are searchable today.
  const [mode, setMode] = useState<"search" | "id">("search");
  const [rawId, setRawId] = useState("");

  // --- Combined user + squad search combobox state ------------------------
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState<PrincipalOption | null>(null);
  const [suggestions, setSuggestions] = useState<PrincipalOption[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debouncedSearchQuery = useDebouncedValue(searchQuery.trim(), 250);
  // Set right before a selection sets searchQuery to "@handle" so the
  // debounced-search effect (below) doesn't treat that as a new query and
  // reopen the dropdown it was just closed by selecting from.
  const justSelectedRef = useRef(false);
  const listboxId = "authorized-publisher-search-listbox";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listAuthorizedPublishers()
      .then((data) => {
        if (cancelled) return;
        setPublishers(data.publishers ?? []);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message ?? "Failed to load authorized publishers");
        setPublishers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }

    const normalized = debouncedSearchQuery.replace(/^@/, "");
    if (normalized.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      setActiveIndex(-1);
      return;
    }

    let cancelled = false;
    setSuggestionsLoading(true);

    // The user search and the squad search run side by side and are combined
    // into one option list. Promise.allSettled (not Promise.all) is the
    // point: a squad-search failure — e.g. a 404 against an admin-service
    // deploy that predates that endpoint — must never blank out or throw
    // past working user results, and vice versa.
    Promise.allSettled([
      listUsers({ query: normalized, pageSize: 8, page: 0 }),
      searchSquads(normalized, 8),
    ])
      .then(([usersResult, squadsResult]) => {
        if (cancelled) return;

        const userOptions: PrincipalOption[] =
          usersResult.status === "fulfilled"
            ? (usersResult.value.users ?? [])
                .filter((user) => !!user.id && !!user.username)
                .map((user) => ({
                  kind: "user",
                  id: user.id,
                  handle: user.username,
                  displayName: user.displayName,
                }))
            : [];

        const squadOptions: PrincipalOption[] =
          squadsResult.status === "fulfilled"
            ? (squadsResult.value.squads ?? [])
                .filter((squad) => !!squad.id)
                .map((squad) => ({
                  kind: "squad",
                  id: squad.id,
                  handle: squad.handle,
                  displayName: squad.displayName,
                }))
            : [];

        setSuggestions([...userOptions, ...squadOptions]);
        setActiveIndex(-1);
        setSuggestionsOpen(true);
      })
      .finally(() => {
        if (!cancelled) setSuggestionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearchQuery]);

  function selectOption(option: PrincipalOption) {
    justSelectedRef.current = true;
    setSelected(option);
    setSearchQuery(option.handle ? `@${option.handle}` : (option.displayName ?? ""));
    setSuggestionsOpen(false);
    setSuggestions([]);
    setActiveIndex(-1);
    if (actionError) setActionError(null);
  }

  function clearSelected() {
    setSelected(null);
    setSearchQuery("");
    setSuggestions([]);
    setSuggestionsOpen(false);
    setActiveIndex(-1);
  }

  function handleSearchInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!suggestionsOpen || suggestions.length === 0) {
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % suggestions.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
        break;
      case "Enter":
        if (activeIndex >= 0 && activeIndex < suggestions.length) {
          event.preventDefault();
          selectOption(suggestions[activeIndex]);
        }
        break;
      case "Escape":
        event.preventDefault();
        setSuggestionsOpen(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  }

  async function handleAdd() {
    const publisherId = mode === "search" ? (selected?.id ?? "") : rawId.trim();
    if (!publisherId) {
      setActionError(
        mode === "search"
          ? "Select a user or squad from the search results, or switch to Add by ID."
          : "Publisher ID is required",
      );
      return;
    }
    // This endpoint is idempotent and doubles as the edit path, so re-adding a
    // publisher already on the list REWRITES its grants with whatever the form's
    // checkboxes currently say. An operator who did not notice the row already
    // existed would silently revoke it, and the symptom (pages dropping out of
    // the index) surfaces weeks later. Make them say so.
    const existing = publishers.find((p) => p.publisherId === publisherId);
    if (existing) {
      const label = existing.handle ? `@${existing.handle}` : publisherId;
      const confirmed = window.confirm(
        `${label} is already on the allowlist. Continuing will REPLACE its grants with ` +
          `indexing=${newIndexing ? "on" : "off"}, dofollow=${newDofollow ? "on" : "off"}, ` +
          `sponsored=${newSponsored ? "on" : "off"}. ` +
          `To change one grant without touching the other, use the toggles in the table.`,
      );
      if (!confirmed) return;
    }

    setAdding(true);
    setActionError(null);
    try {
      await addAuthorizedPublisher(publisherId, newNote.trim(), {
        dofollowLinks: newDofollow,
        searchIndexing: newIndexing,
        sponsoredLinks: newSponsored,
      });
      clearSelected();
      setRawId("");
      setNewNote("");
      setNewDofollow(false);
      setNewIndexing(false);
      setNewSponsored(true);
      setReloadKey((v) => v + 1);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to add publisher");
    } finally {
      setAdding(false);
    }
  }

  /**
   * Toggle one grant on an existing publisher.
   *
   * Sends only the grant being changed — the other is omitted, which the server
   * reads as "leave alone" rather than "revoke". The note is re-sent because
   * this endpoint doubles as the edit path and an absent note would blank it.
   */
  async function handleToggleGrant(
    pub: AuthorizedPublisher,
    grant: "dofollowLinks" | "searchIndexing" | "sponsoredLinks",
    next: boolean,
  ) {
    setGrantingId(pub.publisherId);
    setActionError(null);
    try {
      await addAuthorizedPublisher(pub.publisherId, pub.note ?? "", { [grant]: next });
      setReloadKey((v) => v + 1);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to update grant");
    } finally {
      setGrantingId(null);
    }
  }

  async function handleRemove(pub: AuthorizedPublisher) {
    const label = pub.handle ? `@${pub.handle}` : pub.publisherId;
    if (!window.confirm(`Remove ${label} from the auto-hide allowlist?`)) return;
    setRemovingId(pub.publisherId);
    setActionError(null);
    try {
      await removeAuthorizedPublisher(pub.publisherId);
      setReloadKey((v) => v + 1);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to remove publisher");
    } finally {
      setRemovingId(null);
    }
  }

  const canSubmit = mode === "search" ? selected != null : rawId.trim().length > 0;
  const activeOptionId =
    activeIndex >= 0 && activeIndex < suggestions.length
      ? `authorized-publisher-option-${optionKey(suggestions[activeIndex])}`
      : undefined;
  const indexedSuggestions = suggestions.map((option, index) => ({ option, index }));
  const userSuggestions = indexedSuggestions.filter((s) => s.option.kind === "user");
  const squadSuggestions = indexedSuggestions.filter((s) => s.option.kind === "squad");

  function renderPrincipalOption({ option, index }: { option: PrincipalOption; index: number }) {
    const label = option.displayName || option.handle || option.id;
    return (
      <li
        key={optionKey(option)}
        id={`authorized-publisher-option-${optionKey(option)}`}
        role="option"
        aria-selected={index === activeIndex}
        aria-label={`${optionTypeLabel(option.kind)}: ${label}${option.handle ? ` (@${option.handle})` : ""}`}
        className={index === activeIndex ? styles.comboboxOptionActive : styles.comboboxOption}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => selectOption(option)}
        onMouseEnter={() => setActiveIndex(index)}
      >
        <Avatar size="sm" className="size-[18px] shrink-0" aria-hidden="true">
          <AvatarFallback className="text-[9px]">{initialsForMember(label)}</AvatarFallback>
        </Avatar>
        <span className={styles.comboboxOptionText}>
          <span className={styles.optionTypeBadge}>{optionTypeLabel(option.kind)}</span>
          {option.displayName ? (
            <>
              <span className={styles.identityName}>{option.displayName}</span>{" "}
              {option.handle ? <span className={styles.identityMeta}>@{option.handle}</span> : null}
            </>
          ) : (
            <span className={styles.identityName}>
              {option.handle ? `@${option.handle}` : option.id}
            </span>
          )}
        </span>
      </li>
    );
  }

  return (
    <div className={styles.page}>
      <div>
        <h1 className={styles.title}>Authorized publishers</h1>
        <p className={styles.subtitle}>
          Publishers on this allowlist are exempt from the spam auto-detector&apos;s auto-hide.
          Their content is still flagged for review, but never automatically hidden — use this for
          accounts that run legitimate sponsored or commercial posts.
        </p>
      </div>

      <div className={styles.addSection}>
        <div className={styles.modeToggle} role="group" aria-label="Add publisher by">
          <button
            type="button"
            aria-pressed={mode === "search"}
            className={mode === "search" ? styles.modeButtonActive : styles.modeButton}
            onClick={() => {
              setMode("search");
              setActionError(null);
            }}
          >
            Search
          </button>
          <button
            type="button"
            aria-pressed={mode === "id"}
            className={mode === "id" ? styles.modeButtonActive : styles.modeButton}
            onClick={() => {
              setMode("id");
              setActionError(null);
            }}
          >
            Add by ID
          </button>
        </div>

        <form
          className={styles.addForm}
          onSubmit={(event) => {
            event.preventDefault();
            void handleAdd();
          }}
        >
          {mode === "search" ? (
            <div className={styles.comboboxWrapper}>
              <input
                role="combobox"
                aria-label="Search users and squads to add as publisher"
                aria-expanded={suggestionsOpen && suggestions.length > 0}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={activeOptionId}
                autoComplete="off"
                placeholder="Search by username, squad handle, or name"
                className={styles.comboboxInput}
                value={searchQuery}
                disabled={selected != null}
                onChange={(event) => {
                  setSearchQuery(event.currentTarget.value);
                  setSelected(null);
                }}
                onFocus={() => {
                  if (suggestions.length > 0) setSuggestionsOpen(true);
                }}
                onBlur={() => setSuggestionsOpen(false)}
                onKeyDown={handleSearchInputKeyDown}
              />
              {selected ? (
                <button
                  type="button"
                  className={styles.clearSelectionButton}
                  onClick={clearSelected}
                  aria-label="Clear selected publisher"
                >
                  Change
                </button>
              ) : null}
              {suggestionsOpen && !selected ? (
                <ul
                  id={listboxId}
                  role="listbox"
                  aria-label="Search results"
                  className={styles.comboboxListbox}
                >
                  {suggestionsLoading ? (
                    <li className={styles.comboboxStatus} aria-disabled="true">
                      Searching…
                    </li>
                  ) : suggestions.length === 0 ? (
                    <li className={styles.comboboxStatus} aria-disabled="true">
                      {debouncedSearchQuery.replace(/^@/, "").length < MIN_SEARCH_LENGTH
                        ? "Keep typing to search…"
                        : "No users or squads found"}
                    </li>
                  ) : (
                    <>
                      {userSuggestions.length > 0 ? (
                        <>
                          <li role="presentation" className={styles.comboboxGroupLabel}>
                            Users
                          </li>
                          {userSuggestions.map(renderPrincipalOption)}
                        </>
                      ) : null}
                      {squadSuggestions.length > 0 ? (
                        <>
                          <li role="presentation" className={styles.comboboxGroupLabel}>
                            Squads
                          </li>
                          {squadSuggestions.map(renderPrincipalOption)}
                        </>
                      ) : null}
                    </>
                  )}
                </ul>
              ) : null}
            </div>
          ) : (
            <Input
              aria-label="Publisher ID"
              placeholder="Publisher ID (user or squad principal id)"
              value={rawId}
              onChange={(event) => setRawId(event.currentTarget.value)}
              className={styles.idInput}
            />
          )}
          <Input
            aria-label="Note"
            placeholder="Note (why is this publisher exempt?)"
            value={newNote}
            onChange={(event) => setNewNote(event.currentTarget.value)}
            className={styles.noteInput}
          />
          <label className={styles.grantToggle}>
            <input
              type="checkbox"
              checked={newIndexing}
              onChange={(event) => setNewIndexing(event.currentTarget.checked)}
            />
            Allow search indexing
          </label>
          <label className={styles.grantToggle}>
            <input
              type="checkbox"
              checked={newDofollow}
              onChange={(event) => setNewDofollow(event.currentTarget.checked)}
            />
            Allow dofollow links
          </label>
          <label className={styles.grantToggle}>
            <input
              type="checkbox"
              checked={newSponsored}
              onChange={(event) => setNewSponsored(event.currentTarget.checked)}
            />
            Disclose links as sponsored
          </label>
          <Button type="submit" disabled={adding || !canSubmit}>
            {adding ? "Adding…" : "Add publisher"}
          </Button>
        </form>
        {mode === "id" ? (
          <p className={styles.modeHint}>
            Use this if you already have the principal id — for example, a search result hasn&apos;t
            been indexed yet.
          </p>
        ) : null}
      </div>
      {actionError ? (
        <p className={styles.actionError} role="alert">
          {actionError}
        </p>
      ) : null}

      <section className={styles.tableSection}>
        {loading ? <LoadingOverlay label="Loading authorized publishers..." /> : null}
        <DataTable
          columns="minmax(210px, 1.2fr) minmax(160px, 1.1fr) minmax(115px, 0.7fr) minmax(115px, 0.7fr) minmax(125px, 0.7fr) minmax(110px, 0.6fr) minmax(95px, 0.6fr)"
          header={["Publisher", "Note", "Indexing", "Dofollow", "Sponsored", "Added", "Actions"]}
          headerClassName={styles.tableHeader}
        >
          {publishers.map((pub) => (
            <DataRow key={pub.publisherId} className={styles.tableRow}>
              <PublisherIdentityCell pub={pub} />
              <span className={styles.meta} data-label="Note">
                {pub.note || "—"}
              </span>
              <label className={styles.grantCell} data-label="Indexing">
                <input
                  type="checkbox"
                  checked={pub.searchIndexingGranted === true}
                  disabled={grantingId === pub.publisherId}
                  onChange={(event) =>
                    void handleToggleGrant(pub, "searchIndexing", event.currentTarget.checked)
                  }
                  aria-label={`Search indexing for ${pub.handle ? `@${pub.handle}` : pub.publisherId}`}
                />
                {pub.searchIndexingGranted === true ? "Indexed" : "noindex"}
              </label>
              <label className={styles.grantCell} data-label="Dofollow">
                <input
                  type="checkbox"
                  checked={pub.dofollowLinksGranted === true}
                  disabled={grantingId === pub.publisherId}
                  onChange={(event) =>
                    void handleToggleGrant(pub, "dofollowLinks", event.currentTarget.checked)
                  }
                  aria-label={`Dofollow links for ${pub.handle ? `@${pub.handle}` : pub.publisherId}`}
                />
                {pub.dofollowLinksGranted === true ? "dofollow" : "nofollow"}
              </label>
              <label className={styles.grantCell} data-label="Sponsored">
                <input
                  type="checkbox"
                  checked={pub.sponsoredLinks === true}
                  disabled={grantingId === pub.publisherId}
                  onChange={(event) =>
                    void handleToggleGrant(pub, "sponsoredLinks", event.currentTarget.checked)
                  }
                  aria-label={`Sponsored disclosure for ${pub.handle ? `@${pub.handle}` : pub.publisherId}`}
                />
                {pub.sponsoredLinks === true ? "sponsored" : "undisclosed"}
              </label>
              <span className={styles.meta} data-label="Added">
                {formatDate(pub.createdAt)}
              </span>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.removeButton}
                  disabled={removingId === pub.publisherId}
                  onClick={() => void handleRemove(pub)}
                  aria-label={`Remove ${pub.handle ? `@${pub.handle}` : pub.publisherId}`}
                >
                  <Trash size={14} />
                  {removingId === pub.publisherId ? "Removing…" : "Remove"}
                </button>
              </div>
            </DataRow>
          ))}
        </DataTable>

        {!loading && error ? <div className={styles.emptyState}>{error}</div> : null}
        {!loading && !error && publishers.length === 0 ? (
          <div className={styles.emptyState}>No authorized publishers yet.</div>
        ) : null}
      </section>
    </div>
  );
}
